/* eslint-disable no-console, max-lines */
import { AdditionalPageOptions } from '../interface';

type Validator = (value: any, key: string, parentKey: string) => boolean;
type Schema<T> = {
    [Property in keyof T]: T[Property] extends string | number | undefined
    ? Validator
    : Schema<T[Property]>
};
type RecordKey = string | number | symbol;
type SchemaRecord<T> = { [K in RecordKey]: Schema<T[keyof T]> };

const RECORD_MARKER = '_record_' as RecordKey;
const isSchemaRecord = <T>(object: object): object is SchemaRecord<T> =>
    Boolean(Object.keys(object).length) && Object.keys(object)[0] === RECORD_MARKER;
const isObject = (value: unknown): value is object => value !== null && typeof value === 'object';

const validateRecord = <T>(recordValue: T, schema: Schema<T> | Validator, parentKey: string): boolean => {
    for (const [key, value] of Object.entries(recordValue as object)) {
        if (typeof schema === 'function') {
            const isValid: boolean = schema(value, key, parentKey);

            if (!isValid) {
                return false;
            }

            continue;
        }

        // eslint-disable-next-line no-use-before-define
        const isValid = validate(value, schema, parentKey);

        if (!isValid) {
            console.warn(`One of the value of the "${parentKey}.${key}" has invalid type!`);

            return false;
        }
    }

    return true;
};

const validate = <T, K extends keyof T>(objectOrString: T, schema: Schema<T>, parentKey: string): boolean => {
    for (const [key, validator] of Object.entries(schema)) {
        const current = objectOrString[key as keyof T];

        if (typeof validator === 'function') {
            const isValid = validator(current, key, parentKey);

            if (!isValid) {
                return false;
            }
        }

        if (isObject(objectOrString) && isObject(validator)) {
            if (!(key in (objectOrString))) {
                console.warn(`Property "${parentKey}.${key}" is required!`);

                return false;
            }

            const isValid = isSchemaRecord<T>(validator)
                ? validateRecord(current, validator[RECORD_MARKER], key)
                : validate(current, validator as Schema<T[K]>, key);

            if (!isValid) {
                return false;
            }
        }
    }

    return true;
};

const requiredString = (value: string | undefined, key: string, parentKey: string): boolean => {
    if (!value) {
        console.warn(`Property "${parentKey}.${key}" is required!`);

        return false;
    }

    if (typeof value !== 'string') {
        console.warn(`Property "${parentKey}.${key}" is not string!`);

        return false;
    }

    return true;
};

const requiredNumber = (value: number, key: string, parentKey: string) => {
    if (!value) {
        console.warn(`Property "${parentKey}.${key}" is required!`);

        return false;
    }

    if (typeof value !== 'number') {
        console.warn(`Property "${parentKey}.${key}" is not number!`);

        return false;
    }

    return true;
};

// Example, please implement schema accordint to APO interface
const CMS_SCHEMA: Schema<AdditionalPageOptions> = {
    selectedTariffId: requiredString,
    tariffIds: {
        kdg: {
            [RECORD_MARKER]: requiredString,
        },
        ume: {
            [RECORD_MARKER]: requiredString,
        },
    },
    tariffBenefits: {
        [RECORD_MARKER]: {
            headline: requiredString,
            iconName: requiredString,
            tariffs: {
                [RECORD_MARKER]: requiredString,
            },
        },
    },
    offerSummaryCard: {
        tariffSubline: requiredString,
        button: {
            avc: requiredString,
            available: requiredString,
            unavailable: requiredString,
            existingKip: requiredString,
        } as any,
        additionalText: {
            duration: requiredString,
        },
        notAvailable: {
            headline: requiredString,
            subline: requiredString,
        },
        textAfterPrice: {
            monthly: requiredString,
            continuous: requiredString,
            once: requiredString,
        },
        legalText: {
            discountFootnoteNumber: requiredString,
            discountFootnoteNumberWithTv: requiredString,
            stairway: requiredString,
            fnf: requiredString,
            ne4: requiredString,
            fnfNe4: requiredString,
        },
        cableConnection: {
            text: requiredString,
            button: {
                style: requiredString,
                text: requiredString,
                link: requiredString,
            },
        },
    },
    captcha: {
        [RECORD_MARKER]: requiredString,
    },
    htmlLinks: {
        avcForm: requiredString,
        avcResult: requiredString,
        lmForm: requiredString,
        legalOverlay: requiredString,
    },
    lm: {
        notifications: {
            success: {
                headline: requiredString,
                content: requiredString,
            },
            backendError: {
                headline: requiredString,
                content: requiredString,
            },
            validationError: {
                content: requiredString,
            },
        },
    },
    avcResult: {
        gigacube: {
            linkHref: requiredString,
            text: requiredString,
            button: requiredString,
        },
        dsl: {
            text: requiredString,
            button: requiredString,
        },
        cable: {
            available: {
                headline: requiredString,
                text: requiredString,
                buttonSlower: requiredString,
                buttonTvConnect: requiredString,
                button: requiredString,
                ne4Text: requiredString,
            },
            notAvailable: {
                headline: requiredString,
                cableConnection: {
                    buttonLink: {
                        icon: requiredString,
                        linkHref: requiredString,
                        label: requiredString,
                    },
                    headline: requiredString,
                    text: requiredString,
                },
            },
            slowerAvailable: {
                headline: requiredString,
                text: requiredString,
                button: requiredString,
            },
        },
        fiber: {
            text: requiredString,
            button: requiredString,
        },
    },
    contentUrls: {
        cable: requiredString,
        cableYoung: requiredString,
        cableChange: requiredString,
        fiber: requiredString,
        dsl: requiredString,
        gigacube: requiredString,
        mtan: requiredString,
    } as any,
    errorUrls: {
        friendsAndFamily: requiredString,
    },
    tariffPriceListUrls: {
        vkd: requiredString,
        um: requiredString,
    },
    footnoteUrls: {
        tvConnectStandard: requiredString,
        tvConnectStart: requiredString,
        tvConnectStartPresale: requiredString,
        tvConnectZuhause: requiredString,
        tvConnectZuhausePresale: requiredString,
    },
    isYoung: requiredString,
    isYoungPromoActive: requiredString,
    config: {
        isPlaystationPromoActive: requiredString,
    },
    imageHeader: {
        cableAndFnf: {
            headline: requiredString,
            subline: requiredString,
            imageDesktop: requiredString,
            imageMobile: requiredString,
        },
        cablemaxPlaystation: {
            headline: requiredString,
            subline: requiredString,
            imageDesktop: requiredString,
            imageMobile: requiredString,
        },
        young: {
            headline: requiredString,
            subline: requiredString,
            imageDesktop: requiredString,
            imageMobile: requiredString,
        },
    },
    playstationPromoCard: {
        default: {
            headline: requiredString,
            label: requiredString,
        },
        afterCashbackRegistration: {
            headline: requiredString,
            label: requiredString,
        },
    },
    tvPickerText: requiredString,
    youngPickerText: requiredString,
    youngPromoPickerText: requiredString,
    residentPlusCard: {
        label: requiredString,
        subLabel: requiredString,
    },
};

/** Only for testing! */
export const MOCK_SCHEMA: Schema<any> = {
    key: requiredString,
    number: requiredNumber,
    object: {
        nestedObject: {
            nestedKey: requiredString,
            nestedNumber: requiredNumber,
        },
    },
    recordWithString: {
        [RECORD_MARKER]: requiredString,
    },
    recordWithNumber: {
        [RECORD_MARKER]: requiredNumber,
    },
    recordWithObject: {
        [RECORD_MARKER]: {
            key: requiredString,
            number: requiredNumber,
        },
    },
};

const isCmsConfigValid = (schema: Schema<unknown> = CMS_SCHEMA) => {
    if (!window.additionalPageOptions) {
        console.warn('AdditionalPageOptions is missing!');

        return false;
    }

    return validate(window.additionalPageOptions, schema, 'additionalPageOptions');
};

export default isCmsConfigValid;
